import React, { useEffect, useState, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { trackEvent } from "@/lib/analytics";
import {
  Sparkles,
  Image as ImageIcon,
  Check,
  PenLine,
  Send,
  RefreshCw,
  Loader2,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Rocket,
  X,
  Plus,
} from "lucide-react";

function PostForm({ user, onClose, isModal, initialData }) {
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [images, setImages] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [loadingImages, setLoadingImages] = useState(false);
  const [imageScrollIndex, setImageScrollIndex] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);
  const [validationMsg, setValidationMsg] = useState("");
  const [canPublish, setCanPublish] = useState(false);
  const [publishing, setPublishing] = useState(false);

  const textareaRef = useRef(null);
  const titleRef = useRef(null);
  const failedSubmitRef = useRef(false);

  useEffect(() => {
    if (!textareaRef.current) return;
    textareaRef.current.style.height = "auto";
    textareaRef.current.style.height =
      Math.min(textareaRef.current.scrollHeight, 240) + "px";
  }, [content]);

  useEffect(() => {
    if (!titleRef.current) return;
    titleRef.current.style.height = "auto";
    const lineHeight =
      parseInt(getComputedStyle(titleRef.current).lineHeight) || 24;
    titleRef.current.style.height =
      Math.min(titleRef.current.scrollHeight, lineHeight * 2) + "px";
  }, [title]);

  useEffect(() => {
    if (initialData) {
      setTitle(initialData.title || "");
      setContent(initialData.content || "");
      setSubmitResult(null);
      setValidationMsg("");
      setCanPublish(false);
    }
  }, [initialData]);

  const resetForm = () => {
    setTitle("");
    setContent("");
    setImages([]);
    setSelectedImage(null);
    setLoadingImages(false);
    setImageScrollIndex(0);
    setSubmitResult(null);
    setValidationMsg("");
    setCanPublish(false);
    setPublishing(false);
    failedSubmitRef.current = false;
  };

  useEffect(() => {
    if (failedSubmitRef.current) {
      failedSubmitRef.current = false;
      setSubmitResult(null);
      setValidationMsg("");
      setCanPublish(false);
    }
  }, [title, content]);

  const fetchImages = async () => {
    if (!content.trim() && !title.trim()) {
      toast.error("Write something first so we can suggest images");
      return;
    }
    setLoadingImages(true);
    setImages([]);
    setSelectedImage(null);
    setImageScrollIndex(0);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(
        `${API}/v1/posts/suggest-images`,
        { content: `${title} ${content}`.trim() },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setImages(res.data.images || []);
      trackEvent("create_post_fetch_images", {
        image_count: (res.data.images || []).length,
      });
    } catch {
      toast.error("Failed to fetch images");
    } finally {
      setLoadingImages(false);
    }
  };

  const handleSubmit = async () => {
    if (title.trim().length < 10) {
      toast.error("Title must be at least 10 characters");
      return;
    }
    if (content.trim().length < 50) {
      toast.error("Content must be at least 50 characters");
      return;
    }
    setSubmitting(true);
    setValidationMsg("");
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(
        `${API}/v1/posts/submit`,
        { title: title.trim(), content: content.trim() },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const data = res.data;
      if (data.can_publish) {
        setCanPublish(true);
        trackEvent("create_post_submit", {
          result: "approved",
          post_id: data.post_id,
        });
        toast.success("Post approved! Ready to publish.");
      } else {
        setCanPublish(false);
        setValidationMsg(
          data.validation_message || "Post did not pass quality check.",
        );
        failedSubmitRef.current = true;
      }
      setSubmitResult(data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (Array.isArray(detail)) {
        toast.error(
          detail.map((e) => e.msg || e.message || JSON.stringify(e)).join("; "),
        );
      } else {
        toast.error(
          typeof detail === "string"
            ? detail
            : "Submit failed. Please try again.",
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handlePublish = async () => {
    if (!submitResult?.post_id) return;
    setPublishing(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(
        `${API}/v1/posts/${submitResult.post_id}/publish`,
        {
          post_id: submitResult.post_id,
          selected_image_url: selectedImage || "",
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (res.data.success) {
        trackEvent("create_post_publish", { post_id: submitResult.post_id });
        toast.success(res.data.message || "Post published!");
        onClose();
      } else {
        toast.error("Publish failed");
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (Array.isArray(detail)) {
        toast.error(
          detail.map((e) => e.msg || e.message || JSON.stringify(e)).join("; "),
        );
      } else {
        toast.error(typeof detail === "string" ? detail : "Publish failed");
      }
    } finally {
      setPublishing(false);
    }
  };

  const VISIBLE = 4;
  const totalPages = Math.ceil(images.length / VISIBLE);
  const visibleImages = images.slice(
    imageScrollIndex * VISIBLE,
    imageScrollIndex * VISIBLE + VISIBLE,
  );
  const scrollLeft = () => setImageScrollIndex((p) => Math.max(0, p - 1));
  const scrollRight = () =>
    setImageScrollIndex((p) => Math.min(totalPages - 1, p + 1));

  const isFormReady = title.trim().length >= 10 && content.trim().length >= 200;
  const charCount = content.length;

  const initials = user?.name
    ? user.name
        .split(" ")
        .map((w) => w[0])
        .join("")
        .toUpperCase()
        .slice(0, 2)
    : "U";

  return (
    <>
      <div className="flex items-center gap-3 border-b border-gray-100 px-5 py-3 dark:border-gray-900">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-gray-900 to-gray-600 text-[11px] font-bold text-white ring-2 ring-white dark:from-gray-200 dark:to-gray-400 dark:text-black dark:ring-gray-900">
          {initials}
        </div>
        <div className="flex-1">
          <p className="text-sm font-semibold text-gray-900 dark:text-white">
            {user?.name || "You"}
          </p>
          <p className="text-[11px] text-gray-400 dark:text-gray-500">
            Create a new post
          </p>
        </div>
        <button
          onClick={() => {
            resetForm();
            onClose();
          }}
          className="flex h-8 w-8 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-[#2a2a2a] dark:hover:text-gray-300"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <div
        className={`space-y-8 px-5 py-4 ${isModal ? "flex-1 overflow-y-auto" : ""}`}
      >
        <textarea
          ref={titleRef}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Title"
          maxLength={500}
          rows={1}
          className="w-full resize-none bg-transparent text-base font-semibold leading-relaxed text-gray-900 outline-none placeholder:text-gray-300 dark:text-white dark:placeholder:text-gray-600"
          style={{ maxHeight: 52 }}
        />

        <div>
          <textarea
            ref={textareaRef}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="What's happening?"
            rows={isModal ? 5 : 3}
            className="w-full resize-none bg-transparent text-base leading-relaxed text-gray-700 outline-none placeholder:text-gray-300 dark:text-gray-300 dark:placeholder:text-gray-600"
            style={{ maxHeight: 240 }}
          />
          <div className="flex items-center justify-end gap-2">
            {charCount >= 200 && <Check className="h-3 w-3 text-emerald-500" />}
            <span
              className={`text-[11px] tabular-nums ${
                charCount >= 200
                  ? "text-emerald-500"
                  : charCount >= 120
                    ? "text-amber-400"
                    : "text-gray-300 dark:text-gray-600"
              }`}
            >
              {charCount}
              <span className="text-gray-300 dark:text-gray-600">/200</span>
            </span>
          </div>
        </div>
        <div className="space-y-3">
          <div className="flex items-center justify-between pb-3">
            <span className="flex items-center gap-2 text-[13px] font-medium text-gray-500 dark:text-gray-400">
              <ImageIcon className="h-4 w-4" />
              Cover Image
            </span>
            <div className="flex items-center gap-2">
              {images.length > 0 && (
                <button
                  onClick={fetchImages}
                  disabled={loadingImages}
                  className="flex items-center gap-1.5 rounded-lg border border-gray-200 px-2.5 py-1 text-[11px] font-medium text-gray-500 transition-colors hover:bg-gray-50 disabled:opacity-50 dark:border-[#2a2a2a] dark:text-gray-400 dark:hover:bg-[#2a2a2a]"
                >
                  <RefreshCw
                    className={`h-3 w-3 ${loadingImages ? "animate-spin" : ""}`}
                  />
                  Refresh
                </button>
              )}
              {images.length === 0 && !loadingImages && (
                <button
                  onClick={fetchImages}
                  disabled={loadingImages}
                  className="flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-[11px] font-medium text-gray-500 transition-colors hover:bg-gray-50 disabled:opacity-50 dark:border-[#2a2a2a] dark:text-gray-400 dark:hover:bg-[#2a2a2a]"
                >
                  <Sparkles className="h-3 w-3" />
                  Suggest
                </button>
              )}
            </div>
          </div>
          {loadingImages && (
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              {[1, 2, 3, 4].map((i) => (
                <div
                  key={i}
                  className="aspect-square animate-pulse rounded-md bg-gray-200 dark:bg-[#2a2a2a]"
                />
              ))}
            </div>
          )}

          {!loadingImages && images.length > 0 && (
            <div className="space-y-2.5">
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                {visibleImages.map((img) => (
                  <div
                    key={img.url}
                    onClick={() =>
                      setSelectedImage(
                        selectedImage === img.url ? null : img.url,
                      )
                    }
                    className={`group/img relative aspect-square cursor-pointer overflow-hidden rounded-md border-2 transition-all duration-200 ${
                      selectedImage === img.url
                        ? "border-blue-500 ring-2 ring-blue-500/20"
                        : "border-transparent hover:border-gray-300 dark:hover:border-gray-600"
                    }`}
                  >
                    <img
                      src={img.url}
                      alt={img.title || ""}
                      className="h-full w-full object-cover transition-transform duration-300 group-hover/img:scale-105"
                    />
                    {selectedImage === img.url && (
                      <div className="absolute inset-0 flex items-center justify-center bg-blue-500/20">
                        <div className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-500 shadow-lg">
                          <Check className="h-3.5 w-3.5 text-white" />
                        </div>
                      </div>
                    )}
                    {selectedImage !== img.url && (
                      <div className="absolute inset-0 bg-black/0 transition-colors duration-200 group-hover/img:bg-black/10" />
                    )}
                  </div>
                ))}
              </div>

              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-3">
                  <button
                    onClick={scrollLeft}
                    disabled={imageScrollIndex === 0}
                    className="flex h-7 w-7 items-center justify-center rounded-full border border-gray-200 text-gray-400 transition-colors hover:bg-gray-50 hover:text-gray-600 disabled:opacity-30 dark:border-gray-700 dark:hover:bg-gray-800 dark:hover:text-white"
                  >
                    <ChevronLeft className="h-3.5 w-3.5" />
                  </button>
                  <div className="flex gap-1.5">
                    {Array.from({ length: totalPages }).map((_, i) => (
                      <button
                        key={i}
                        onClick={() => setImageScrollIndex(i)}
                        className={`h-1.5 rounded-full transition-all duration-300 ${
                          i === imageScrollIndex
                            ? "w-5 bg-blue-500"
                            : "w-1.5 bg-gray-300 hover:bg-gray-400 dark:bg-gray-600 dark:hover:bg-gray-500"
                        }`}
                      />
                    ))}
                  </div>
                  <button
                    onClick={scrollRight}
                    disabled={imageScrollIndex >= totalPages - 1}
                    className="flex h-7 w-7 items-center justify-center rounded-full border border-gray-200 text-gray-400 transition-colors hover:bg-gray-50 hover:text-gray-600 disabled:opacity-30 dark:border-gray-700 dark:hover:bg-gray-800 dark:hover:text-white"
                  >
                    <ChevronRight className="h-3.5 w-3.5" />
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {submitResult && !canPublish && (
          <div className="space-y-2.5 rounded-xl border border-amber-200/60 bg-amber-50/50 p-4 dark:border-amber-900/40 dark:bg-amber-950/20">
            <div className="flex items-center gap-2.5">
              <AlertCircle className="h-4 w-4 shrink-0 text-amber-500" />
              <span className="text-[13px] font-semibold text-amber-600 dark:text-amber-400">
                Needs Improvement
              </span>
              <div className="ml-auto flex gap-3 text-[11px] text-amber-500/70 dark:text-amber-400/60">
                {submitResult.quality_score != null && (
                  <span>Quality {submitResult.quality_score}/10</span>
                )}
                {submitResult.engagement_score != null && (
                  <span>Engagement {submitResult.engagement_score}/10</span>
                )}
              </div>
            </div>
            {submitResult.validation_message && (
              <p className="text-[12.5px] leading-relaxed text-amber-700/80 dark:text-amber-300/70">
                {submitResult.validation_message}
              </p>
            )}
            {submitResult.suggestions?.length > 0 && (
              <ul className="space-y-1.5 border-t border-amber-200/40 pt-2.5 dark:border-amber-800/30">
                {submitResult.suggestions.map((s, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-2 text-[12px] leading-relaxed text-amber-700/70 dark:text-amber-300/60"
                  >
                    <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-amber-400 dark:bg-amber-500" />
                    {s}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {submitResult && canPublish && (
          <div className="flex items-center gap-2.5 rounded-xl border border-emerald-200/60 bg-emerald-50/50 px-4 py-3 dark:border-emerald-900/40 dark:bg-emerald-950/20">
            <div className="flex h-5 w-5 items-center justify-center rounded-full bg-emerald-500">
              <Check className="h-3 w-3 text-white" />
            </div>
            <span className="text-[13px] font-semibold text-emerald-600 dark:text-emerald-400">
              Approved
            </span>
            <div className="ml-auto flex gap-3 text-[11px] text-emerald-500/70 dark:text-emerald-400/60">
              <span>Quality {submitResult.quality_score}/10</span>
              <span>Engagement {submitResult.engagement_score}/10</span>
            </div>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between border-t border-gray-100 px-5 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] dark:border-[#2a2a2a]">
        <div className="flex items-center gap-1">
          <button
            onClick={fetchImages}
            className="flex h-9 w-9 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-blue-50 hover:text-blue-500 dark:hover:bg-blue-950/40 dark:hover:text-blue-400"
          >
            <ImageIcon className="h-[18px] w-[18px]" />
          </button>
          <button className="flex h-9 w-9 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-amber-50 hover:text-amber-500 dark:hover:bg-amber-950/40 dark:hover:text-amber-400">
            <PenLine className="h-[18px] w-[18px]" />
          </button>
          <button className="flex h-9 w-9 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-violet-50 hover:text-violet-500 dark:hover:bg-violet-950/40 dark:hover:text-violet-400">
            <Sparkles className="h-[18px] w-[18px]" />
          </button>
        </div>

        {!canPublish ? (
          <button
            onClick={handleSubmit}
            disabled={submitting || !isFormReady}
            className={`flex items-center gap-1.5 rounded-full px-5 py-2 text-[13px] font-semibold transition-all duration-200 active:scale-[0.97] disabled:cursor-not-allowed ${
              isFormReady && !submitting
                ? "bg-blue-600 text-white hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600"
                : "bg-gray-100 text-gray-400 dark:bg-[#2a2a2a] dark:text-gray-500"
            }`}
          >
            {submitting ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Send className="h-3.5 w-3.5" />
            )}
            <span>{submitting ? "Reviewing…" : "Submit"}</span>
          </button>
        ) : (
          <button
            onClick={handlePublish}
            disabled={publishing}
            className="flex items-center gap-1.5 rounded-full bg-emerald-500 px-5 py-2 text-[13px] font-semibold text-white transition-all duration-200 hover:bg-emerald-600 active:scale-[0.97] disabled:opacity-70"
          >
            {publishing ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Rocket className="h-3.5 w-3.5" />
            )}
            <span>{publishing ? "Publishing…" : "Publish"}</span>
          </button>
        )}
      </div>
    </>
  );
}

function DesktopCreatePost({ user, initialData, onEditClose }) {
  const [expanded, setExpanded] = useState(false);
  const [activeData, setActiveData] = useState(null);
  const [prevInitialData, setPrevInitialData] = useState(null);

  // Derived-state-from-props: when initialData changes to a new value, store it locally and expand
  if (initialData !== prevInitialData) {
    setPrevInitialData(initialData);
    if (initialData) {
      setActiveData(initialData);
      setExpanded(true);
    }
  }

  const isExpanded = expanded;
  const expandBodyRef = useRef(null);
  const [bodyHeight, setBodyHeight] = useState(0);

  const recalcHeight = useCallback(() => {
    if (expandBodyRef.current) {
      setBodyHeight(expandBodyRef.current.scrollHeight);
    }
  }, []);

  useEffect(() => {
    recalcHeight();
  }, [expanded, recalcHeight]);

  useEffect(() => {
    if (!expandBodyRef.current) return;
    const ro = new ResizeObserver(() => recalcHeight());
    ro.observe(expandBodyRef.current);
    return () => ro.disconnect();
  }, [recalcHeight]);

  const initials = user?.name
    ? user.name
        .split(" ")
        .map((w) => w[0])
        .join("")
        .toUpperCase()
        .slice(0, 2)
    : "U";

  return (
    <div className="mb-6 hidden w-5/6 overflow-hidden rounded-2xl border border-gray-100 bg-white transition-shadow duration-300 dark:border-[#2a2a2a] dark:bg-[#1c1c1e] md:block">
      {!isExpanded && (
        <div
          onClick={() => setExpanded(true)}
          className="cursor-pointer space-y-3 px-5 py-4 transition-colors duration-200"
        >
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-gray-800 to-gray-500 text-[12px] font-bold tracking-tight text-white dark:from-gray-300 dark:to-gray-100 dark:text-gray-900">
                {initials}
              </div>
              <div className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-white bg-emerald-400 dark:border-gray-950" />
            </div>
            <span className="text-[15px] text-gray-400 dark:text-gray-500">
              What&rsquo;s happening?
            </span>
          </div>

          <div className="flex items-center justify-between border-t border-gray-100/80 pt-3 dark:border-gray-800/40">
            <div className="flex items-center gap-0.5">
              <span className="flex h-8 w-8 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-blue-50 hover:text-blue-500 dark:hover:bg-blue-950/30 dark:hover:text-blue-400">
                <ImageIcon className="h-[17px] w-[17px]" />
              </span>
              <span className="flex h-8 w-8 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-amber-50 hover:text-amber-500 dark:hover:bg-amber-950/30 dark:hover:text-amber-400">
                <PenLine className="h-[17px] w-[17px]" />
              </span>
              <span className="flex h-8 w-8 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-violet-50 hover:text-violet-500 dark:hover:bg-violet-950/30 dark:hover:text-violet-400">
                <Sparkles className="h-[17px] w-[17px]" />
              </span>
            </div>
            <div className="flex items-center gap-1.5 rounded-full bg-gray-200/60 px-4 py-1.5 text-[13px] font-semibold text-gray-400 dark:bg-[#2a2a2a] dark:text-gray-500">
              <Send className="h-3.5 w-3.5" />
              <span>Submit</span>
            </div>
          </div>
        </div>
      )}

      <div
        style={{
          maxHeight: isExpanded ? bodyHeight + "px" : "0px",
          opacity: isExpanded ? 1 : 0,
        }}
        className="duration-[350ms] ease-[cubic-bezier(0.4,0,0.2,1)] overflow-hidden transition-[max-height,opacity]"
      >
        <div ref={expandBodyRef}>
          <PostForm
            user={user}
            onClose={() => {
              setExpanded(false);
              setActiveData(null);
              onEditClose?.();
            }}
            isModal={false}
            initialData={activeData}
          />
        </div>
      </div>
    </div>
  );
}

function MobileCreatePost({
  user,
  mobileOpen = false,
  onMobileClose,
  initialData,
}) {
  const [open, setOpen] = useState(false);
  const [visible, setVisible] = useState(false);
  const [dragY, setDragY] = useState(0);
  const [sheetHeight, setSheetHeight] = useState(0);
  const dragStartRef = useRef(null);
  const sheetRef = useRef(null);

  const openSheet = () => {
    setSheetHeight(Math.round(window.innerHeight * 0.95));
    setOpen(true);
    requestAnimationFrame(() => requestAnimationFrame(() => setVisible(true)));
  };

  const closeSheet = useCallback(() => {
    setVisible(false);
    setDragY(0);
    setTimeout(() => {
      setOpen(false);
      onMobileClose?.();
    }, 340);
  }, [onMobileClose]);

  const onTouchStart = useCallback((e) => {
    dragStartRef.current = e.touches[0].clientY;
  }, []);

  const onTouchMove = useCallback((e) => {
    if (dragStartRef.current === null) return;
    const delta = e.touches[0].clientY - dragStartRef.current;
    if (delta > 0) {
      setDragY(delta);
    }
  }, []);

  const onTouchEnd = useCallback(() => {
    if (dragStartRef.current === null) return;

    if (dragY > 100) {
      closeSheet();
    } else {
      setDragY(0);
    }
    dragStartRef.current = null;
  }, [dragY, closeSheet]);

  useEffect(() => {
    if (open) {
      const scrollY = window.scrollY;
      document.body.classList.add("modal-open");
      document.body.style.top = `-${scrollY}px`;
    } else {
      const scrollY = document.body.style.top;
      document.body.classList.remove("modal-open");
      document.body.style.top = "";
      window.scrollTo(0, parseInt(scrollY || "0") * -1);
    }
    return () => {
      const scrollY = document.body.style.top;
      document.body.classList.remove("modal-open");
      document.body.style.top = "";
      if (scrollY) window.scrollTo(0, parseInt(scrollY) * -1);
    };
  }, [open]);

  const prevMobileOpen = useRef(false);
  useEffect(() => {
    if (mobileOpen && !prevMobileOpen.current) {
      openSheet();
    }
    prevMobileOpen.current = mobileOpen;
  }, [mobileOpen]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => e.key === "Escape" && closeSheet();
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, closeSheet]);

  return (
    <>
      {open &&
        createPortal(
          <div
            className="fixed inset-0 z-[100] touch-none md:hidden"
            style={{ overscrollBehavior: "contain" }}
          >
            <div
              onClick={closeSheet}
              className="absolute inset-0 backdrop-blur-sm transition-all duration-300"
              style={{
                backgroundColor: "rgba(0,0,0,0.4)",
                opacity: visible ? 1 : 0,
              }}
            />

            <div
              ref={sheetRef}
              className="absolute left-0 right-0 flex touch-auto flex-col rounded-t-2xl bg-[#F9F9F9] shadow-[0_-8px_40px_rgba(0,0,0,0.15)] dark:bg-[#121212] dark:shadow-[0_-8px_40px_rgba(0,0,0,0.5)]"
              style={{
                top: window.innerHeight - sheetHeight + "px",
                height: sheetHeight + "px",
                transform: visible
                  ? `translateY(${dragY}px)`
                  : `translateY(${sheetHeight}px)`,
                transition:
                  dragY > 0
                    ? "none"
                    : "transform 0.32s cubic-bezier(0.32, 0.72, 0, 1)",
              }}
            >
              <div
                className="flex cursor-grab justify-center pb-1 pt-3 active:cursor-grabbing"
                onTouchStart={onTouchStart}
                onTouchMove={onTouchMove}
                onTouchEnd={onTouchEnd}
              >
                <div className="h-1.5 w-12 rounded-full bg-gray-300 dark:bg-gray-700" />
              </div>

              <div
                className="flex items-center justify-between rounded-xl border-b border-gray-200 px-4 py-2.5 dark:border-[#2a2a2a]"
                onTouchStart={onTouchStart}
                onTouchMove={onTouchMove}
                onTouchEnd={onTouchEnd}
              >
                <button
                  onClick={closeSheet}
                  className="text-[14px] font-medium text-gray-500 transition-colors hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
                >
                  Cancel
                </button>
                <p className="text-[15px] font-bold text-gray-900 dark:text-white">
                  New Post
                </p>
                <div className="w-14" />
              </div>

              <div className="flex flex-1 flex-col overflow-hidden">
                <PostForm
                  user={user}
                  onClose={closeSheet}
                  isModal={true}
                  initialData={initialData}
                />
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}

function CreatePost({
  user,
  navVisible,
  mobileOpen,
  onMobileClose,
  initialData,
  onEditClose,
}) {
  return (
    <>
      <div className="hidden w-full md:flex md:items-center md:justify-center">
        <DesktopCreatePost
          user={user}
          initialData={initialData}
          onEditClose={onEditClose}
        />
      </div>
      <MobileCreatePost
        user={user}
        mobileOpen={mobileOpen}
        onMobileClose={onMobileClose}
        initialData={initialData}
      />
    </>
  );
}

export default CreatePost;
export { MobileCreatePost };
